<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { Check } from "@lucide/vue";
import type { SavedLocation } from "@/stores/location.store";
import type { TemperatureUnit } from "@/stores/settings.store";
import {
  fittingShortcutCount,
  type LocationWeatherSummary,
} from "@/utils/locationSummary";
import LocationWeatherBadge from "./LocationWeatherBadge.vue";

const props = defineProps<{
  places: SavedLocation[];
  menuOpen: boolean;
  selectedId: string | null;
  summaries: Map<string, LocationWeatherSummary>;
  loading: boolean;
  unit: TemperatureUnit;
  now: number;
}>();

const emit = defineEmits<{
  select: [id: string];
  more: [trigger: HTMLButtonElement];
}>();

const container = ref<HTMLElement | null>(null);
const measure = ref<HTMLElement | null>(null);
const visibleCount = ref(0);
const visible = computed(() => props.places.slice(0, visibleCount.value));
const remaining = computed(() => props.places.length - visibleCount.value);
let observer: ResizeObserver | undefined;

/** Measure shortcuts and reserve room for the overflow control. */
function layout() {
  if (!container.value || !measure.value) return;
  const widths = Array.from(measure.value.children).map(
    (item) => item.getBoundingClientRect().width,
  );
  visibleCount.value = fittingShortcutCount(
    widths,
    container.value.clientWidth,
    88,
  );
}

watch(
  () => [props.places, props.selectedId],
  async () => {
    await nextTick();
    layout();
  },
  { deep: true },
);

onMounted(() => {
  observer = new ResizeObserver(layout);
  if (container.value) observer.observe(container.value);
  if (measure.value) observer.observe(measure.value);
  layout();
});

onUnmounted(() => observer?.disconnect());
</script>
<template>
  <nav
    ref="container"
    class="relative hidden min-w-[88px] flex-1 items-center gap-2 lg:flex"
    aria-label="Saved locations"
  >
    <button
      v-for="place in visible"
      :key="place.id"
      type="button"
      class="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-control px-3 py-2 text-[13px] whitespace-nowrap hover:bg-app-elevated hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      :class="selectedId === place.id ? 'text-text-primary' : 'text-text-muted'"
      :aria-pressed="selectedId === place.id"
      @click="emit('select', place.id)"
    >
      <Check
        v-if="selectedId === place.id"
        :size="12"
        class="shrink-0 stroke-[2.5] text-navigation-active"
        aria-hidden="true"
      />
      <span
        class="max-w-32 overflow-hidden text-ellipsis"
        :title="place.name"
        >{{ place.name }}</span
      >
      <LocationWeatherBadge
        :summary="summaries.get(place.id)"
        :loading="loading"
        :unit="unit"
        :now="now"
      />
    </button>
    <button
      v-if="remaining"
      class="inline-flex min-h-10 w-[88px] shrink-0 items-center justify-center gap-2 rounded-control px-1 py-2 text-[13px] whitespace-nowrap text-text-muted hover:bg-app-elevated hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="menuOpen"
      aria-controls="header-location-menu"
      :aria-label="`Show all saved locations, ${remaining} more`"
      @click="emit('more', $event.currentTarget as HTMLButtonElement)"
    >
      +{{ remaining }} more
    </button>
    <div
      class="pointer-events-none invisible absolute inset-0 overflow-hidden"
      aria-hidden="true"
      inert
    >
      <div ref="measure" class="pointer-events-none invisible flex w-max">
        <div
          v-for="place in places"
          :key="place.id"
          class="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-control px-3 py-2 text-[13px] whitespace-nowrap text-text-muted hover:bg-app-elevated hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Check
            v-if="selectedId === place.id"
            :size="12"
            class="shrink-0 stroke-[2.5] text-navigation-active"
          />
          <span class="max-w-32 overflow-hidden text-ellipsis">{{
            place.name
          }}</span
          ><span class="w-[66px] shrink-0" />
        </div>
      </div>
    </div>
  </nav>
</template>
